import { Injectable, EventEmitter } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Enums, IEnums } from 'app/app.enums';
import { CacheService } from './cache.service';
import { ConfigService } from './config.service';
import * as Models from '@shared/models';
import * as _ from 'underscore';

export interface ILanguageService {
  getDefaultLanguage(): Promise<Models.ILanguageInfo>;
  getTranslations(languageId: number): Promise<Models.ITranslationInfo>;
  getTranslationInfo(): Models.ITranslationInfo;
  setLanguage(languageId: number): Promise<Models.ILanguageInfo>;
  getTranslationByKey(key: string, languageId: number): string;
}

interface AppData {
  translations?: Record<string, unknown>;
}

@Injectable({ providedIn: 'root' })
export class TranslationService implements ILanguageService {
  languageChanged = new EventEmitter(false);
  translationInfo: Models.ITranslationInfo = {
    isLoaded: false,
    languageId: null,
    defaultLanguageId: null,
    languageInfo: null,
    translations: null,
  };

  private enums: IEnums = Enums;
  private serviceBase = '';
  private language: Models.ILanguageInfo = {
    languageId: null,
    name: null,
  };

  constructor(
    private http: HttpClient,
    private cache: CacheService,
    private configService: ConfigService
  ) {
    this.configService.Config.subscribe((config) => {
      if (!config) return;
      this.serviceBase = config.apiUri;
    });
  }

  getDefaultLanguage(): Promise<Models.ILanguageInfo> {
    const lang = window.navigator.language || '';
    let languageId: number = 1;
    switch (lang) {
      case this.enums.language.englishCode:
        languageId = this.enums.language.english;
        break;
      case this.enums.language.frenchCode:
        languageId = this.enums.language.french;
        break;
      default:
        languageId = this.enums.language.english;
        break;
    }
    return new Promise<Models.ILanguageInfo>((resolve, reject) => {
      this.http
        .post<Models.ILanguageInfo>(this.serviceBase + '/language/getLanguageById?languageId=' + languageId, {})
        .toPromise()
        .then((response: Models.ILanguageInfo | undefined) => {
          const languageInfo: Models.ILanguageInfo = {
            languageId: response?.languageId ?? null,
            name: response?.name ?? null,
          };
          this.language.languageId = languageInfo.languageId;
          this.language.name = languageInfo.name;
          this.cache.setCache<Models.ILanguageInfo>(this.enums.cacheKeys.languageData, languageInfo);
          if (languageId && languageInfo.languageId !== null) {
            this.getTranslations(languageInfo.languageId).then(() => {
              this.updateLanguage();
              resolve(languageInfo);
            });
          }
        })
        .catch((err: unknown) => {
          reject(err);
        });
    });
  }

  getTranslations(languageId: number | null): Promise<Models.ITranslationInfo> {
    return new Promise<Models.ITranslationInfo>((resolve, reject) => {
      this.http
        .post<{ defaultLanguageId: number | null; language: Models.ILanguageInfo | null }>(
          this.serviceBase + '/language/getTranslationsByLanguageId?languageId=' + languageId,
          {}
        )
        .toPromise()
        .then((response: { defaultLanguageId: number | null; language: Models.ILanguageInfo | null } | undefined) => {
          const appData = (window as unknown as { app?: AppData })['app'];
          const languageTranslations: Models.ITranslationsMap = this.dataToTranslationMap(
            appData?.['translations'] ?? {}
          );
          const translationInfo: Models.ITranslationInfo = {
            isLoaded: true,
            languageId: languageId,
            defaultLanguageId: response?.defaultLanguageId ?? null,
            languageInfo: response?.language ?? null,
          };

          this.cache.setCache<Models.ITranslationInfo>(this.enums.cacheKeys.translationData, translationInfo);

          translationInfo.translations = languageTranslations;
          resolve(translationInfo);
        })
        .catch((err: unknown) => {
          reject(err);
        });
    });
  }

  getTranslationByKey(key: string, languageId: number): string {
    if (this.translationInfo.translations === null) this.loadTranslationsFromStorage();
    if (!this.translationInfo.translations || !this.translationInfo.translations[key]) return key + ': NOT FOUND';
    const translationValues = this.translationInfo.translations[key] as unknown as Record<number, string>;
    const translation: string =
      translationValues[languageId] ||
      translationValues[this.translationInfo.defaultLanguageId ?? 1] ||
      key + ': NOT FOUND';
    return translation;
  }

  getTranslationInfo(): Models.ITranslationInfo {
    this.loadTranslationsFromStorage();
    return this.translationInfo;
  }

  loadTranslationsFromStorage(): void {
    const translationData = this.cache.retrieveCache<Models.ITranslationInfo>(this.enums.cacheKeys.translationData);
    const appData = (window as unknown as { app?: { translations?: Record<string, unknown> } })['app'];
    const languageTranslations: Models.ITranslationsMap = this.dataToTranslationMap(appData?.['translations'] ?? {});
    if (translationData !== null) {
      this.updateTranslationInfo(
        {
          isLoaded: true,
          languageId: translationData.languageId,
          defaultLanguageId: translationData.defaultLanguageId,
          languageInfo: translationData.languageInfo,
          translations: languageTranslations,
        },
        true
      );
    } else {
      this.getDefaultLanguage();
    }
  }

  setLanguage(languageId: number): Promise<Models.ILanguageInfo> {
    return new Promise<Models.ILanguageInfo>((resolve, reject) => {
      this.http
        .post<Models.ILanguageInfo>(this.serviceBase + '/language/getLanguageById?languageId=' + languageId, {})
        .toPromise()
        .then((response) => {
          const languageInfo: Models.ILanguageInfo = {
            languageId: response?.languageId ?? null,
            name: response?.name ?? null,
          };
          this.language.languageId = languageInfo.languageId;
          this.language.name = languageInfo.name;
          this.cache.setCache<Models.ILanguageInfo>(this.enums.cacheKeys.languageData, languageInfo);
          if (languageId && languageInfo.languageId !== null) {
            this.getTranslations(languageInfo.languageId).then(() => {
              this.updateLanguage();
              resolve(languageInfo);
            });
          }
        })
        .catch((err: unknown) => {
          reject(err);
        });
    });
  }

  private dataToTranslationMap(data: Record<string, unknown>): Models.ITranslationsMap {
    const result: Models.ITranslationsMap = {};
    _.each(data, (itemData: unknown, key: string) => {
      result[key] = itemData as Models.ITranslationValues[];
    });
    return result;
  }

  private updateTranslationInfo(newInfo: Models.ITranslationInfo, force?: boolean): void {
    if (force || !this.translationInfo || JSON.stringify(this.translationInfo) !== JSON.stringify(newInfo)) {
      this.translationInfo = newInfo;
    }
  }

  updateLanguage(): void {
    this.languageChanged.emit();
  }
}
